import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar/bottom-bar';
import {FancyTextItemBottomBarOptions, TranscriptItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import {BottomBarItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/bottom-bar-item-color-fill';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openTranscriptPresetsModal} from '@Modals/transcript-presets-modal/transcript-presets-modal.types';
import {openPosterEditorItemFontSizeModal} from '@Modals/poster-editor-item-font-size-modal';
import {openPosterEditorTextItemFontModal} from '@Modals/poster-editor-text-item-font-modal';
import {openPosterEditorShadowModal} from '@Modals/poster-editor-item-shadow-modal';
import {openPosterEditorItemBackgroundPanel} from '@Modals/poster-editor-item-background-modal';
import {openPosterEditorTranscriptTextFillModal} from '@Modals/poster-editor-transcript-text-fill-modal/poster-editor-transcript-text-fill-modal.types';
import {useEditorPrefetchTextMobileModules} from '@Hooks/poster-editor/useEditorPrefetchTextMobileModules';
import {openPosterEditorTranscriptOutlineModal} from '@Modals/poster-editor-transcript-outline-modal/poster-editor-transcript-outline-modal.types';
import {openEditPosterSubtitlesModal} from '@Modals/edit-poster-subtitles-modal';
import {openPosterEditorTranscriptActiveWordModal} from '@Modals/poster-editor-transcript-active-word-modal/poster-editor-transcript-active-word-modal.types';
import {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import type {BottomBarItemProps} from '@Components/contextual-menu/components/bottom-bar-item';
import {openPosterEditorAlignmentModal} from '@Modals/poster-editor-item-alignment-modal';
import {openPosterEditorItemLetterSpacingModal} from '@Modals/poster-editor-item-letter-spacing-modal';
import {openPosterEditorTranscriptLetterCaseModal} from '@Modals/poster-editor-transcript-letter-case-modal/poster-editor-transcript-letter-case-modal.types';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './transcript-bottom-bar.module.scss';
import {useGetKeyFromSubtitlePropertiesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitlePropertiesForTranscriptItem';
import {useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';

export function TranscriptBottomBar(): ReactElement | null {
  const activeTranscriptItem = useGetSelectedTranscriptItem();
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;

  useEditorPrefetchTextMobileModules();

  const animationStyle = useGetKeyFromSubtitlePropertiesForTranscriptItem('animationStyle');
  if (!animationStyle) {
    throw new Error('Transcript item does not have animationStyle property in its subtitle objects');
  }

  const fillColor = useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem('fillColor');
  if (fillColor === undefined) {
    throw new Error('Transcript item subtitle objects do not have fillColor property in their sentenceTextStyles object');
  }

  const fillType = useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem('fillType');
  if (fillType === undefined) {
    throw new Error('Transcript item subtitle objects do not have fillType property in their sentenceTextStyles object');
  }

  const isLocked = useGetKeyFromActiveItem('lockMovementX');

  if (!activeTranscriptItem) {
    return null;
  }

  const pulsatingDotForCopyStyles = (): ReactElement | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: TranscriptItemBottomBarOptions): void => {
    switch (itemId) {
      case TranscriptItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case TranscriptItemBottomBarOptions.DELETE:
        deleteItemByID(activeTranscriptItem.uid);
        break;
      case TranscriptItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case TranscriptItemBottomBarOptions.PRESETS:
        openTranscriptPresetsModal();
        break;
      case TranscriptItemBottomBarOptions.SIZE:
        openPosterEditorItemFontSizeModal();
        break;
      case TranscriptItemBottomBarOptions.FONT:
        openPosterEditorTextItemFontModal();
        break;
      case TranscriptItemBottomBarOptions.SHADOW:
        openPosterEditorShadowModal();
        break;
      case TranscriptItemBottomBarOptions.SENTENCE_FILL:
        openPosterEditorTranscriptTextFillModal();
        break;
      case TranscriptItemBottomBarOptions.BACKGROUND:
        openPosterEditorItemBackgroundPanel();
        break;
      case TranscriptItemBottomBarOptions.SENTENCE_OUTLINE:
        openPosterEditorTranscriptOutlineModal();
        break;
      case TranscriptItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case TranscriptItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case TranscriptItemBottomBarOptions.SUBTITLES:
        openEditPosterSubtitlesModal();
        break;
      case TranscriptItemBottomBarOptions.HIGHLIGHT_WORD:
        openPosterEditorTranscriptActiveWordModal();
        break;
      case TranscriptItemBottomBarOptions.LETTER_SPACING:
        openPosterEditorItemLetterSpacingModal();
        break;
      case TranscriptItemBottomBarOptions.LETTER_CASE:
        openPosterEditorTranscriptLetterCaseModal();
        break;
      case TranscriptItemBottomBarOptions.ALIGNMENT:
        openPosterEditorAlignmentModal();
        break;
      default:
    }
  };

  const getSentenceTextColorFillContent = (): ReactElement => {
    return <BottomBarItemColorFill type={fillType} color={fillColor} />;
  };

  const getBottomBarItems = (): BottomBarItemProps[] => {
    const items: BottomBarItemProps[] = [
      {
        id: TranscriptItemBottomBarOptions.PRESETS,
        icon: 'icon-ai-subtitles-presets',
        text: window.i18next.t('pmwjs_presets'),
      },
      {
        id: TranscriptItemBottomBarOptions.SUBTITLES,
        icon: 'icon-captions',
        text: window.i18next.t('pmwjs_subtitles'),
      },
    ];

    if (animationStyle !== SubtitleTemplateType.SIMPLE && animationStyle !== SubtitleTemplateType.ONE_WORD) {
      items.push({
        id: TranscriptItemBottomBarOptions.HIGHLIGHT_WORD,
        icon: 'icon-active-word',
        text: animationStyle === SubtitleTemplateType.PROGRESS ? window.i18next.t('pmwjs_highlight_words') : window.i18next.t('pmwjs_highlight_word'),
      });
    }

    items.push(
      {
        id: TranscriptItemBottomBarOptions.FONT,
        icon: 'icon-font',
        text: window.i18next.t('pmwjs_font'),
      },
      {
        id: TranscriptItemBottomBarOptions.SIZE,
        icon: 'icon-text-size',
        text: window.i18next.t('pmwjs_size'),
      },
      {
        id: TranscriptItemBottomBarOptions.SENTENCE_FILL,
        customContent: getSentenceTextColorFillContent(),
        text: window.i18next.t('pmwjs_fill'),
      },
      {
        id: TranscriptItemBottomBarOptions.POSITION,
        icon: 'icon-prints-stack',
        text: window.i18next.t('pmwjs_position'),
      },
      {
        id: TranscriptItemBottomBarOptions.SENTENCE_OUTLINE,
        icon: 'icon-outline',
        text: window.i18next.t('pmwjs_outline'),
      },
      {
        id: TranscriptItemBottomBarOptions.ALIGNMENT,
        icon: 'icon-align-center',
        text: window.i18next.t('pmwjs_alignment'),
      },
      {
        id: TranscriptItemBottomBarOptions.LETTER_CASE,
        icon: 'icon-sentence-case',
        text: window.i18next.t('pmwjs_letter_case'),
      },
      {
        id: TranscriptItemBottomBarOptions.LETTER_SPACING,
        icon: 'icon-letter-spacing',
        text: window.i18next.t('pmwjs_letter_spacing'),
      },
      {
        id: TranscriptItemBottomBarOptions.BACKGROUND,
        icon: 'icon-background',
        text: window.i18next.t('pmwjs_background'),
      },
      {
        id: TranscriptItemBottomBarOptions.SHADOW,
        icon: 'icon-shadow',
        text: window.i18next.t('pmwjs_shadow'),
      },
      {
        id: TranscriptItemBottomBarOptions.COPY_STYLES,
        icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
        text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
        isSelected: copiedStylesState === CopyStyleState.COPIED,
        tag: pulsatingDotForCopyStyles(),
      },

      {
        id: TranscriptItemBottomBarOptions.LOCK,
        icon: isLocked ? 'icon-lock' : 'icon-unlock',
        text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
      },
      {
        id: TranscriptItemBottomBarOptions.DUPLICATE,
        icon: 'icon-duplicate',
        text: window.i18next.t('pmwjs_duplicate'),
      },
      {
        id: TranscriptItemBottomBarOptions.DELETE,
        icon: 'icon-delete',
        text: window.i18next.t('pmwjs_delete'),
        className: 'color-danger-100',
      }
    );

    return items;
  };

  return <BottomBar id="poster-editing-default-bottom-bar" onClick={onClickHandler} items={getBottomBarItems()} />;
}
